import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSupabase } from '@/components/providers/SupabaseProvider';
import type { GuestLandlordStatus } from '@/types/database';

// Query keys
export const statusKeys = {
  all: ['landlord-status'] as const,
  byLandlord: (landlordId: string) => [...statusKeys.all, landlordId] as const,
};

/** What landlord_heartbeat() returns. */
export interface HostChatStatus {
  /** The host's own choice "available for chat" (guests see online only with a fresh heartbeat). */
  is_online: boolean;
  is_chat_enabled: boolean;
  last_seen_at: string | null;
}

/** Heartbeat period while a dashboard tab is visible (guests: online = seen in the last 3 min). */
const HEARTBEAT_MS = 60_000;

/**
 * The host's chat status + presence heartbeat.
 *
 * Mounted by DashboardLayout (every dashboard page) and the Messages page —
 * one shared query. While a dashboard tab is visible it calls
 * landlord_heartbeat() every minute (server clock); when the tab is hidden or
 * the browser is closed the heartbeat stops and guests see "offline" within
 * 3 minutes. The online / chat switches are the host's choice and stick
 * (they used to be forced back on by every page open).
 */
export function useOnlineStatus(landlordId: string | undefined) {
  const queryClient = useQueryClient();
  const { isLoading: authLoading } = useSupabase();
  const key = statusKeys.byLandlord(landlordId || '');

  const { data: status, isLoading } = useQuery({
    queryKey: key,
    queryFn: async (): Promise<HostChatStatus | null> => {
      const { data, error } = await supabase.rpc('landlord_heartbeat', { p_online: null });
      if (error) {
        if (import.meta.env.DEV) console.debug('[OnlineStatus] heartbeat error:', error.message);
        throw error;
      }
      return (data as HostChatStatus) ?? null;
    },
    // Wait for the Clerk JWT (the RPC refuses anonymous callers).
    enabled: !!landlordId && !authLoading,
    refetchInterval: HEARTBEAT_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: HEARTBEAT_MS / 2,
    retry: 1,
  });

  const setOnlineMutation = useMutation({
    mutationFn: async (online: boolean) => {
      const { data, error } = await supabase.rpc('landlord_heartbeat', { p_online: online });
      if (error) throw error;
      return data as HostChatStatus;
    },
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  const setChatEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!landlordId) throw new Error('No landlord ID');
      const { data, error } = await supabase
        .from('landlord_status')
        .update({ is_chat_enabled: enabled })
        .eq('landlord_id', landlordId)
        .select('is_online, is_chat_enabled, last_seen_at')
        .single();
      if (error) throw error;
      return data as HostChatStatus;
    },
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  const toggleOnline = useCallback(async () => {
    await setOnlineMutation.mutateAsync(!(status?.is_online ?? true));
  }, [status?.is_online, setOnlineMutation]);

  const toggleChatEnabled = useCallback(async () => {
    await setChatEnabledMutation.mutateAsync(!(status?.is_chat_enabled ?? true));
  }, [status?.is_chat_enabled, setChatEnabledMutation]);

  return {
    status,
    isLoading,
    isOnline: status?.is_online ?? true,
    isChatEnabled: status?.is_chat_enabled ?? true,
    toggleOnline,
    toggleChatEnabled,
    isUpdating: setOnlineMutation.isPending || setChatEnabledMutation.isPending,
    updateError: setOnlineMutation.error || setChatEnabledMutation.error,
  };
}

/**
 * Hook za dohvat statusa iznajmljivača (za goste)
 * Uses secure RPC function to bypass RLS (anon cannot read landlord_status directly)
 */
export function useLandlordStatusForGuest(accessToken: string | undefined) {
  return useQuery({
    queryKey: ['landlord-status', 'guest', accessToken],
    queryFn: async () => {
      if (!accessToken) return null;

      const { data, error } = await supabase.rpc('get_guest_landlord_status', {
        p_access_token: accessToken,
      });

      if (error || !data) {
        const fallback: GuestLandlordStatus = {
          is_online: false,
          is_chat_enabled: true,
          chat_paused: false,
          host_phone: null,
          auto_reply_message: null,
          last_seen_at: null,
        };
        return fallback;
      }

      return data as GuestLandlordStatus;
    },
    enabled: !!accessToken,
    refetchInterval: 10000,
  });
}

/**
 * Format last seen time
 */
export function formatLastSeen(lastSeenAt: string | null, locale: string = 'hr-HR'): string {
  if (!lastSeenAt) return '';

  const date = new Date(lastSeenAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) {
    return locale === 'hr-HR' ? 'Online' : 'Online';
  }

  if (diffMins < 60) {
    return locale === 'hr-HR'
      ? `Zadnji put viđen prije ${diffMins} min`
      : `Last seen ${diffMins} min ago`;
  }

  if (diffHours < 24) {
    return locale === 'hr-HR'
      ? `Zadnji put viđen prije ${diffHours}h`
      : `Last seen ${diffHours}h ago`;
  }

  return locale === 'hr-HR'
    ? `Zadnji put viđen ${date.toLocaleDateString('hr-HR')}`
    : `Last seen ${date.toLocaleDateString('en-US')}`;
}

export default useOnlineStatus;
